import { onlineManager } from "@tanstack/react-query";
import type { ColumnDef, Table } from "@tanstack/react-table";
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { useAtomValue } from "jotai";
import { useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";

import { CrossIllutration } from "~/components/icons/cross";
import { NoResultIllustration } from "~/components/icons/no-result";
import { OrderClaimBadge } from "~/components/order-status-badge/order-claim-badge";
import { OrderClaimReasonBadge } from "~/components/order-status-badge/order-claim-reason-badge";
import { OrderFulfillmentBadge } from "~/components/order-status-badge/order-fulfillment-badge";
import { OrderRefundBadge } from "~/components/order-status-badge/order-refund-badge";
import { OrderReshipBadge } from "~/components/order-status-badge/order-reship-badge";
import { OrderReturnBadge } from "~/components/order-status-badge/order-return-badge";
import { OrderShipmentBadge } from "~/components/order-status-badge/order-shipment-badge";
import { useResetList } from "~/components/order-table/use-reset-list";
import { Button } from "~/components/ui/button";
import { cn } from "~/components/ui/cn";
import { ShopifyErrorIcon } from "~/components/ui/icons/shopify-error";
import { SortIcon } from "~/components/ui/icons/sort";
import { SortBottomIcon } from "~/components/ui/icons/sort-bottom";
import { SortTopIcon } from "~/components/ui/icons/sort-top";
import {
  Tooltip,
  TooltipArrow,
  TooltipContent,
  TooltipTrigger,
} from "~/components/ui/tooltip";
import type {
  OrderTagClaim,
  OrderTagClaimReason,
  OrderTagFulfillment,
  OrderTagRefund,
  OrderTagReship,
  OrderTagReturn,
  OrderTagShipment,
  TrackingSubStatus,
} from "~/graphql/graphql-new.gen";
import { brandState } from "~/stores/brand";
import { transformPrice } from "~/utils/currency";
import { relativeDate } from "~/utils/date";
import { usePagination } from "~/views/orders/use-pagination";
import { useSearch } from "~/views/orders/use-search";
import { TabEntry, useTabs } from "~/views/orders/use-tabs";

import { OrderItemImages } from "./order-item-images";
import { OrderTableNoOrder } from "./order-table-no-order";
import { OrderTableOffline } from "./order-table-offline";
import { OrderTableRow } from "./order-table-row";
import { OrderTableSkeleton } from "./order-table-skeleton";
import { useFixedHeader } from "./use-fixed-header";
import { useSorting } from "./use-sorting";

export type OrderDataImage = {
  imageUrl: string;
  quantity: number;
};

export type OrderData = {
  claim: OrderTagClaim[];
  claimCreatedAt: Date | null;
  claimReason: OrderTagClaimReason[];
  createdAt: Date;
  fulfillment: OrderTagFulfillment;
  hasSynchronizationError: boolean;
  id: string;
  items: OrderDataImage[];
  parcelTrackingSubStatus: TrackingSubStatus | null;
  parcelHasTrackingStucked: boolean;
  recipientName: string;
  refund: OrderTagRefund;
  requiredAction: boolean;
  reship: OrderTagReship;
  return: OrderTagReturn[];
  shipment: OrderTagShipment[];
  total: {
    amount: number;
    currency: string;
  };
};

type OrderTableProps = {
  data: OrderData[];
  error: unknown;
  isLoading: boolean;
};

const SKELETON_DATA = Array.from({ length: 30 }, (e, i) => i);

export const OrderTable: React.FC<OrderTableProps> = ({
  data,
  error,
  isLoading,
}) => {
  const { resetPagination } = usePagination();
  const { isSearchEmpty } = useSearch();
  const [_, setSearchParams] = useSearchParams();
  const { sorting, setSorting, trackSorting } = useSorting();
  const { selectedTab } = useTabs();
  const { i18n, t } = useTranslation();

  const brand = useAtomValue(brandState);

  const tableRef = useRef<HTMLTableElement>(null);

  const columns: ColumnDef<OrderData>[] = useMemo(
    () => [
      {
        accessorKey: "id",
        header: () => (
          <div className="p-4 text-left uppercase">{t("orders.table.id")}</div>
        ),
        cell: ({ row }) => (
          <div
            className={cn(
              "flex items-center gap-x-1 text-xs text-green-900 sm:w-[136px] sm:p-4 sm:text-sm",
              {
                "font-bold": row.original.requiredAction,
                "text-red-600": row.original.hasSynchronizationError,
              },
            )}
          >
            {row.original.hasSynchronizationError ? (
              <ShopifyErrorIcon className="size-4 shrink-0" />
            ) : null}
            <div className="truncate">{row.getValue("id")}</div>
          </div>
        ),
        enableSorting: false,
        size: 136,
      },
      {
        accessorKey: "createdAt",
        header: ({ header }) => (
          <Tooltip>
            <TooltipTrigger className="group flex cursor-pointer justify-between gap-4 p-4 text-left hover:bg-gray-50">
              <span className="uppercase">{t("orders.table.created_at")}</span>
              {header.column.getIsSorted() === "asc" ? (
                <SortTopIcon className="size-4" />
              ) : null}
              {header.column.getIsSorted() === "desc" ? (
                <SortBottomIcon className="size-4" />
              ) : null}
              <SortIcon
                className={cn("invisible size-4 group-hover:visible", {
                  hidden: header.column.getIsSorted() !== false,
                })}
              />
            </TooltipTrigger>
            <TooltipContent sideOffset={8}>
              <div className="p-1">
                {header.column.getIsSorted() === "asc"
                  ? t("orders.table.headers.sort.date_asc")
                  : t("orders.table.headers.sort.date_desc")}
              </div>
              <TooltipArrow />
            </TooltipContent>
          </Tooltip>
        ),
        cell: ({ row }) => (
          <div
            className={cn("flex items-center text-xs sm:p-4 sm:text-sm", {
              "text-gray-700": row.original.requiredAction,
              "text-[#9DA0A1]": !row.original.requiredAction,
            })}
          >
            {relativeDate(
              row.getValue("createdAt"),
              i18n.language === "fr" ? "fr" : "en",
              brand?.timezone ?? undefined,
            )}
          </div>
        ),
      },
      {
        accessorKey: "recipientName",
        header: ({ header }) => (
          <Tooltip>
            <TooltipTrigger className="group flex cursor-pointer justify-between gap-4 p-4 text-left hover:bg-gray-50">
              <span className="uppercase">{t("orders.table.customer")}</span>
              {header.column.getIsSorted() === "asc" ? (
                <SortTopIcon className="size-4" />
              ) : null}
              {header.column.getIsSorted() === "desc" ? (
                <SortBottomIcon className="size-4" />
              ) : null}
              <SortIcon
                className={cn("invisible size-4 group-hover:visible", {
                  hidden: header.column.getIsSorted() !== false,
                })}
              />
            </TooltipTrigger>
            <TooltipContent sideOffset={8}>
              <div className="p-1">
                {header.column.getIsSorted() === "asc"
                  ? t("orders.table.headers.sort.alphanumeric_asc")
                  : t("orders.table.headers.sort.alphanumeric_desc")}
              </div>
              <TooltipArrow />
            </TooltipContent>
          </Tooltip>
        ),
        cell: ({ row }) => (
          <div
            className={cn(
              "flex items-center font-semibold sm:w-[144px] sm:p-4 sm:font-normal",
              {
                "text-gray-700": row.original.requiredAction,
                "text-[#9DA0A1]": !row.original.requiredAction,
              },
            )}
          >
            <div className="truncate">{row.getValue("recipientName")}</div>
          </div>
        ),
        size: 144,
      },
      {
        accessorKey: "items",
        header: () => (
          <div className="p-4 text-left uppercase">
            {t("orders.table.items")}
          </div>
        ),
        cell: ({ row }) => (
          <div className="flex flex-row flex-nowrap gap-x-1 sm:p-4">
            <OrderItemImages items={row.original.items} />
          </div>
        ),
        enableSorting: false,
        size: 144,
      },
      {
        accessorKey: "total",
        header: ({ header }) => (
          <Tooltip>
            <TooltipTrigger className="group flex cursor-pointer justify-between gap-4 p-4 text-right hover:bg-gray-50">
              {header.column.getIsSorted() === "asc" ? (
                <SortTopIcon className="size-4" />
              ) : null}
              {header.column.getIsSorted() === "desc" ? (
                <SortBottomIcon className="size-4" />
              ) : null}
              <SortIcon
                className={cn("invisible size-4 group-hover:visible", {
                  hidden: header.column.getIsSorted() !== false,
                })}
              />
              <span className="uppercase">{t("orders.table.total")}</span>
            </TooltipTrigger>
            <TooltipContent sideOffset={8}>
              <div className="p-1">
                {header.column.getIsSorted() === "asc"
                  ? t("orders.table.headers.sort.amount_asc")
                  : t("orders.table.headers.sort.amount_desc")}
              </div>
              <TooltipArrow />
            </TooltipContent>
          </Tooltip>
        ),
        cell: ({ row }) => (
          <div
            className={cn("flex flex-col items-end text-sm uppercase sm:p-4", {
              "text-gray-700": row.original.requiredAction,
              "text-[#9DA0A1]": !row.original.requiredAction,
            })}
          >
            {transformPrice(
              row.original.total.amount,
              i18n.language === "fr" ? "fr" : "en",
              row.original.total.currency,
            )}
          </div>
        ),
      },
      {
        accessorKey: "fulfillment",
        header: ({ header }) => (
          <Tooltip>
            <TooltipTrigger className="group flex cursor-pointer justify-between gap-4 p-4 text-left hover:bg-gray-50">
              <span className="uppercase">{t("orders.table.fulfillment")}</span>
              {header.column.getIsSorted() === "asc" ? (
                <SortTopIcon className="size-4" />
              ) : null}
              {header.column.getIsSorted() === "desc" ? (
                <SortBottomIcon className="size-4" />
              ) : null}
              <SortIcon
                className={cn("invisible size-4 group-hover:visible", {
                  hidden: header.column.getIsSorted() !== false,
                })}
              />
            </TooltipTrigger>
            <TooltipContent sideOffset={8}>
              <div className="p-1">
                {header.column.getIsSorted() === "asc"
                  ? t("orders.table.headers.sort.alphanumeric_asc")
                  : t("orders.table.headers.sort.alphanumeric_desc")}
              </div>
              <TooltipArrow />
            </TooltipContent>
          </Tooltip>
        ),
        cell: ({ row }) => (
          <div className="flex items-center sm:p-4">
            <OrderFulfillmentBadge tag={row.original.fulfillment} />
          </div>
        ),
      },
      {
        accessorKey: "shipment",
        header: () => (
          <div className="p-4 text-left uppercase">
            {t("orders.table.delivery")}
          </div>
        ),
        cell: ({ row }) => (
          <div className="flex flex-col gap-1 sm:p-4">
            {row.original.shipment.map((shipment) => (
              <OrderShipmentBadge
                key={shipment}
                tag={shipment}
                trackingSubStatus={row.original.parcelTrackingSubStatus}
                hasTrackingStucked={row.original.parcelHasTrackingStucked}
              />
            ))}
          </div>
        ),
        enableSorting: false,
      },
      {
        accessorKey: "claim",
        header: ({ header }) => (
          <Tooltip>
            <TooltipTrigger className="group flex cursor-pointer justify-between gap-4 p-4 text-left hover:bg-gray-50">
              <span className="uppercase">{t("orders.table.claim")}</span>
              {header.column.getIsSorted() === "asc" ? (
                <SortTopIcon className="size-4" />
              ) : null}
              {header.column.getIsSorted() === "desc" ? (
                <SortBottomIcon className="size-4" />
              ) : null}
              <SortIcon
                className={cn("invisible size-4 group-hover:visible", {
                  hidden: header.column.getIsSorted() !== false,
                })}
              />
            </TooltipTrigger>
            <TooltipContent sideOffset={8}>
              <div className="p-1">
                {header.column.getIsSorted() === "asc"
                  ? t("orders.table.headers.sort.alphanumeric_asc")
                  : t("orders.table.headers.sort.alphanumeric_desc")}
              </div>
              <TooltipArrow />
            </TooltipContent>
          </Tooltip>
        ),
        cell: ({ row }) => (
          <div className="flex items-center whitespace-nowrap sm:p-4">
            {row.original.claim.map((claim) => (
              <OrderClaimBadge key={claim} tag={claim} />
            ))}
          </div>
        ),
      },
      {
        accessorKey: "claimCreatedAt",
        header: ({ header }) => (
          <Tooltip>
            <TooltipTrigger className="group flex cursor-pointer justify-between gap-4 p-4 text-left hover:bg-gray-50">
              <span className="uppercase">
                {t("orders.table.claim_created_at")}
              </span>
              {header.column.getIsSorted() === "asc" ? (
                <SortTopIcon className="size-4" />
              ) : null}
              {header.column.getIsSorted() === "desc" ? (
                <SortBottomIcon className="size-4" />
              ) : null}
              <SortIcon
                className={cn("invisible size-4 group-hover:visible", {
                  hidden: header.column.getIsSorted() !== false,
                })}
              />
            </TooltipTrigger>
            <TooltipContent sideOffset={8}>
              <div className="p-1">
                {header.column.getIsSorted() === "asc"
                  ? t("orders.table.headers.sort.date_asc")
                  : t("orders.table.headers.sort.date_desc")}
              </div>
              <TooltipArrow />
            </TooltipContent>
          </Tooltip>
        ),
        cell: ({ row }) => (
          <div
            className={cn("flex items-center text-xs sm:p-4 sm:text-sm", {
              "text-gray-700": row.original.requiredAction,
              "text-[#9DA0A1]": !row.original.requiredAction,
            })}
          >
            {row.getValue("claimCreatedAt")
              ? relativeDate(
                  row.getValue("claimCreatedAt"),
                  i18n.language === "fr" ? "fr" : "en",
                  brand?.timezone ?? undefined,
                )
              : ""}
          </div>
        ),
      },
      {
        accessorKey: "claimReason",
        header: ({ header }) => (
          <Tooltip>
            <TooltipTrigger className="group flex cursor-pointer justify-between gap-4 p-4 text-left hover:bg-gray-50">
              <span className="uppercase">
                {t("orders.table.claim_reason")}
              </span>
              {header.column.getIsSorted() === "asc" ? (
                <SortTopIcon className="size-4" />
              ) : null}
              {header.column.getIsSorted() === "desc" ? (
                <SortBottomIcon className="size-4" />
              ) : null}
              <SortIcon
                className={cn("invisible size-4 group-hover:visible", {
                  hidden: header.column.getIsSorted() !== false,
                })}
              />
            </TooltipTrigger>
            <TooltipContent sideOffset={8}>
              <div className="p-1">
                {header.column.getIsSorted() === "asc"
                  ? t("orders.table.headers.sort.alphanumeric_asc")
                  : t("orders.table.headers.sort.alphanumeric_desc")}
              </div>
              <TooltipArrow />
            </TooltipContent>
          </Tooltip>
        ),
        cell: ({ row }) => (
          <div className="flex items-center whitespace-nowrap sm:p-4">
            {row.original.claimReason.map((claimReason) => (
              <OrderClaimReasonBadge key={claimReason} tag={claimReason} />
            ))}
          </div>
        ),
      },
      {
        accessorKey: "refund",
        header: ({ header }) => (
          <Tooltip>
            <TooltipTrigger className="group flex cursor-pointer justify-between gap-4 p-4 text-left hover:bg-gray-50">
              <span className="uppercase">{t("orders.table.refund")}</span>
              {header.column.getIsSorted() === "asc" ? (
                <SortTopIcon className="size-4" />
              ) : null}
              {header.column.getIsSorted() === "desc" ? (
                <SortBottomIcon className="size-4" />
              ) : null}
              <SortIcon
                className={cn("invisible size-4 group-hover:visible", {
                  hidden: header.column.getIsSorted() !== false,
                })}
              />
            </TooltipTrigger>
            <TooltipContent sideOffset={8}>
              <div className="p-1">
                {header.column.getIsSorted() === "asc"
                  ? t("orders.table.headers.sort.alphanumeric_asc")
                  : t("orders.table.headers.sort.alphanumeric_desc")}
              </div>
              <TooltipArrow />
            </TooltipContent>
          </Tooltip>
        ),
        cell: ({ row }) => (
          <div className="flex items-center whitespace-nowrap sm:p-4">
            <OrderRefundBadge tag={row.original.refund} />
          </div>
        ),
      },
      {
        accessorKey: "reship",
        header: ({ header }) => (
          <Tooltip>
            <TooltipTrigger className="group flex cursor-pointer justify-between gap-4 p-4 text-left hover:bg-gray-50">
              <span className="uppercase">{t("orders.table.reship")}</span>
              {header.column.getIsSorted() === "asc" ? (
                <SortTopIcon className="size-4" />
              ) : null}
              {header.column.getIsSorted() === "desc" ? (
                <SortBottomIcon className="size-4" />
              ) : null}
              <SortIcon
                className={cn("invisible size-4 group-hover:visible", {
                  hidden: header.column.getIsSorted() !== false,
                })}
              />
            </TooltipTrigger>
            <TooltipContent sideOffset={8}>
              <div className="p-1">
                {header.column.getIsSorted() === "asc"
                  ? t("orders.table.headers.sort.alphanumeric_asc")
                  : t("orders.table.headers.sort.alphanumeric_desc")}
              </div>
              <TooltipArrow />
            </TooltipContent>
          </Tooltip>
        ),
        cell: ({ row }) => (
          <div className="flex items-center whitespace-nowrap sm:p-4">
            <OrderReshipBadge tag={row.original.reship} />
          </div>
        ),
      },
      {
        accessorKey: "return",
        header: () => (
          <div className="p-4 text-left uppercase">
            {t("orders.table.return")}
          </div>
        ),
        cell: ({ row }) => (
          <div className="flex items-center whitespace-nowrap sm:p-4">
            {row.original.return.map((ret) => (
              <OrderReturnBadge key={ret} tag={ret} />
            ))}
          </div>
        ),
        enableSorting: false,
      },
    ],
    [brand?.timezone, i18n.language, t],
  );

  const columnPinning =
    selectedTab === TabEntry.Return ? ["id", "return"] : ["id"];

  const table = useReactTable({
    data,
    columns,
    state: {
      columnPinning: {
        left: columnPinning,
      },
      columnOrder:
        selectedTab === TabEntry.Return
          ? ["id", "return", "createdAt", "recipientName", "items", "total"]
          : ["id", "createdAt", "recipientName", "items", "total"],
      sorting,
    },
    enableColumnPinning: true,
    enableSortingRemoval: false,
    manualSorting: true,
    sortDescFirst: true,
    getCoreRowModel: getCoreRowModel(),
    onSortingChange: (updater) => {
      // make sure updater is callable (to avoid typescript warning)
      if (typeof updater !== "function") return;

      const newSorting = updater(table.getState().sorting);
      setSorting(newSorting);
      resetPagination();
      trackSorting(newSorting);

      setSearchParams((prev) => {
        prev.delete("after");
        prev.delete("before");
        prev.set("sort", JSON.stringify(newSorting));

        return prev;
      });
    },
  });

  const handleScroll = (event: React.UIEvent<HTMLDivElement>) => {
    if (tableRef.current === null) return;

    if (
      event.currentTarget.scrollLeft > 0 &&
      tableRef.current?.getAttribute("data-scroll") !== "true"
    ) {
      tableRef.current?.setAttribute("data-scroll", "true");
    }
    if (
      event.currentTarget.scrollLeft === 0 &&
      tableRef.current?.getAttribute("data-scroll") !== "false"
    ) {
      tableRef.current?.setAttribute("data-scroll", "false");
    }
  };

  const [tableWrapperRef, headerRef] = useFixedHeader();

  return (
    <>
      <div id="scrollIntoView-element"></div>
      <div className="overflow-hidden sm:rounded sm:border sm:border-gray-100">
        <div
          ref={tableWrapperRef}
          className="no-scrollbar w-full overflow-x-auto"
          onScroll={handleScroll}
        >
          <div
            ref={tableRef}
            className="group/table whitespace-nowrap sm:table sm:border-collapse"
          >
            {/* Table header */}
            <div
              ref={headerRef}
              className="sticky z-[2] hidden sm:table-header-group"
            >
              {table.getHeaderGroups().map((headerGroup) => (
                <div key={headerGroup.id} data-table-row className="table-row">
                  {/* Table row */}
                  {headerGroup.headers.map((header) => (
                    <div
                      key={header.id}
                      data-table-cell
                      data-pinned={
                        header.column.getIsPinned() ? "true" : "false"
                      }
                      className={cn(
                        "table-cell align-middle sticky shrink-0 items-center bg-white text-xs font-semibold text-gray-700",
                        {
                          "group-data-[scroll=true]/table:shadow-[inset_-1px_0_0_#ececec,_inset_0_-1px_0_#d8d9da]":
                            header.column.getIsPinned() &&
                            header.column.getPinnedIndex() ===
                              columnPinning.length - 1,
                          "z-[1]": header.column.getIsPinned(),
                          "shadow-[inset_0_-1px_0_#d8d9da]":
                            !isSearchEmpty && table.getRowModel().rows.length,
                        },
                      )}
                      style={{
                        width: header.column.getIsPinned()
                          ? header.column.getSize()
                          : undefined,
                        top: 0,
                        left: header.column.getIsPinned()
                          ? header.column.getStart()
                          : undefined,
                      }}
                      {...(header.column.getCanSort()
                        ? { onClick: header.column.getToggleSortingHandler() }
                        : {})}
                    >
                      {/* Table cell */}
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext(),
                          )}
                    </div>
                  ))}
                </div>
              ))}
            </div>

            {/* Table body */}
            <div className="block space-y-3 sm:table-row-group sm:space-y-0">
              {isLoading && !error && !isSearchEmpty
                ? SKELETON_DATA.map((row) => <OrderTableSkeleton key={row} />)
                : null}
              {!isLoading &&
              !error &&
              !isSearchEmpty &&
              table.getRowModel().rows.length
                ? table
                    .getRowModel()
                    .rows.map((row) => (
                      <OrderTableRow
                        key={row.id}
                        row={row}
                        columnPinning={columnPinning}
                      />
                    ))
                : null}
            </div>
          </div>
        </div>
      </div>
      <OrderTableStatus error={error} isLoading={isLoading} table={table} />
    </>
  );
};

function OrderTableStatus({
  error,
  isLoading,
  table,
}: {
  error: unknown;
  isLoading: boolean;
  table: Table<OrderData>;
}) {
  const { isSearchActive, isSearchEmpty } = useSearch();
  const { resetList } = useResetList();
  const { selectedTab } = useTabs();
  const { t } = useTranslation();

  if (!onlineManager.isOnline()) {
    return <OrderTableOffline />;
  }

  if (isLoading) return null;

  if (error) {
    return (
      <div className="mt-20 flex w-full flex-col items-center space-y-4">
        <CrossIllutration className="size-20 text-gray-900" />
        <h4 className="text-2xl font-bold text-gray-900">
          {t("orders.table.empty_state.error.an_error_occurred")}
        </h4>
        <Button type="button" onClick={() => window.location.reload()}>
          {t("orders.table.empty_state.error.refresh_page")}
        </Button>
      </div>
    );
  }

  if (
    !isSearchEmpty &&
    selectedTab !== TabEntry.Filters &&
    !isSearchActive &&
    !table.getRowModel().rows.length
  ) {
    return <OrderTableNoOrder />;
  }

  if (
    !isSearchEmpty &&
    selectedTab === TabEntry.Filters &&
    !table.getRowModel().rows.length
  ) {
    return (
      <div className="mt-20 flex w-full flex-col items-center space-y-4">
        <NoResultIllustration className="size-20" />
        <div className="space-y-1 text-center">
          <h4 className="text-2xl font-bold text-gray-900">
            {t("orders.table.empty_state.no_result.no_result_available")}
          </h4>
          <p className="text-sm">
            {t(
              "orders.table.empty_state.no_result.change_your_filter_criterias",
            )}
          </p>
        </div>

        <Button type="button" onClick={() => resetList()}>
          {t("orders.table.empty_state.no_result.clear_all_filters")}
        </Button>
      </div>
    );
  }

  if (!isSearchEmpty && isSearchActive && !table.getRowModel().rows.length) {
    return (
      <div className="mt-20 flex w-full flex-col items-center space-y-4">
        <NoResultIllustration className="size-20" />
        <div className="space-y-1 text-center">
          <h4 className="text-2xl font-bold text-gray-900">
            {t("orders.table.empty_state.no_result.no_result_available")}
          </h4>
          <p className="text-sm">
            {t(
              "orders.table.empty_state.no_result.change_your_search_criterias",
            )}
          </p>
        </div>
      </div>
    );
  }

  return null;
}
